import { type ReactElement, type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { type Result } from '@vello/core';
import { Avatar, Badge, Button, Notice, PageHeader, TextInput, EmptyState } from './primitives';
import { ConfirmDialog, type ConfirmSpec } from './overlays';
import { Field } from './forms';
import { FileInput } from './file-input';
import { Tabs, TabPanel, KeyValue } from './structure';
import { Page, Row, Stack } from './layout';
import { Card } from './card';
import { Text } from './text';
import { PanelHead } from './panel-parts';
import { RadioGroup } from './check-radio';
import { LoadingPanel } from './loading-panel';
import { CodeColumns } from './admin-b-parts';
import { initialsOf } from './sidebar-nav';
import { THEME_PREFERENCES, type ThemePreference, useThemePreference } from '../theme-preference';

/**
 * "Meu perfil" (D806, doc 35 §3.2): the signed-in operator's own page, the same in the five consoles (Admin, Central, Loja, Frota,
 * Balcão). Four tabs, each with at most one primary action:
 *
 *  - **Perfil** - the optional photo (choose a JPEG → preview → send; change; remove after the DS confirm), first and last name (save),
 *    and the e-mail read-only: it is the login, an administrator changes it (owner decision 1).
 *  - **Preferências** - the theme (the same three choices as the operator menu) and the language, saved on the account
 *    (`PATCH /v1/me { locale }`) and applied at once through the console's `setLang`.
 *  - **Segurança** - change the password (current, new, confirm, a current authenticator code; the other devices are signed out), the
 *    two-step state and new recovery codes (a current code; the codes are shown ONCE with copy and a .txt download).
 *  - **Sessões ativas** - where the account is signed in, "esta sessão", end one other session, or all the others after a confirm.
 *
 * Neutral on purpose: this package holds no language and no API client. The console passes its own `t` (every id of
 * {@link OPERATOR_PROFILE_MESSAGE_IDS}, in each language it ships), its vocabulary (the server's decision and refusal codes in words, the
 * `refusal.operator.profile` surface) and the api-client groups it already has (`runtime.auth`, `runtime.operatorAuth`), which match
 * the structural interfaces below.
 */

/** Every message id the page reads through the console's `t`. A console's profile catalog is typed `Record<OperatorProfileMessageId, …>`. */
export const OPERATOR_PROFILE_MESSAGE_IDS = [
  'profile.title',
  'profile.subtitle',
  'profile.tabs.aria',
  'profile.tab.profile',
  'profile.tab.preferences',
  'profile.tab.security',
  'profile.tab.sessions',
  'profile.loading',
  'profile.loadFailed',
  'profile.retry',
  'profile.cancel',
  'profile.error.generic',
  'profile.photo.title',
  'profile.photo.hint',
  'profile.photo.field',
  'profile.photo.choose',
  'profile.photo.replace',
  'profile.photo.empty',
  'profile.photo.clear',
  'profile.photo.preview',
  'profile.photo.send',
  'profile.photo.remove',
  'profile.photo.removeConfirm.title',
  'profile.photo.removeConfirm.body',
  'profile.photo.removeConfirm.confirm',
  'profile.name.title',
  'profile.name.first',
  'profile.name.last',
  'profile.name.required',
  'profile.name.save',
  'profile.email.label',
  'profile.email.hint',
  'profile.prefs.theme',
  'profile.prefs.theme.system',
  'profile.prefs.theme.light',
  'profile.prefs.theme.dark',
  'profile.prefs.language',
  'profile.prefs.language.hint',
  'profile.prefs.language.saved',
  'profile.password.title',
  'profile.password.current',
  'profile.password.new',
  'profile.password.newHint',
  'profile.password.confirm',
  'profile.password.mismatch',
  'profile.password.submit',
  'profile.code.label',
  'profile.code.hint',
  'profile.code.invalid',
  'profile.mfa.title',
  'profile.mfa.state',
  'profile.mfa.remaining',
  'profile.mfa.notSealed',
  'profile.codes.warning',
  'profile.codes.generate',
  'profile.codes.title',
  'profile.codes.once',
  'profile.codes.copy',
  'profile.codes.copied',
  'profile.codes.copyFailed',
  'profile.codes.download',
  'profile.codes.fileName',
  'profile.codes.done',
  'profile.sessions.title',
  'profile.sessions.subtitle',
  'profile.sessions.current',
  'profile.sessions.signedIn',
  'profile.sessions.lastActive',
  'profile.sessions.end',
  'profile.sessions.endAria',
  'profile.sessions.none',
  'profile.sessions.endAll',
  'profile.sessions.endAllConfirm.title',
  'profile.sessions.endAllConfirm.body',
  'profile.sessions.endAllConfirm.confirm',
] as const;

export type OperatorProfileMessageId = (typeof OPERATOR_PROFILE_MESSAGE_IDS)[number];

/** The refusal surface the page reads its error sentences from (`@vello/vocabulary`). */
export const OPERATOR_PROFILE_REFUSAL_SURFACE = 'refusal.operator.profile';
/** D854: the reason code worded for a 429 that carries its wait (`{seconds}`; `problem.operator_profile_shared`). */
export const OPERATOR_PROFILE_RETRY_IN = 'RATE_LIMITED_RETRY_IN';

type Vars = Readonly<Record<string, string | number>>;

/** What the page knows about a failed call - the part of an api-client `ApiError` it reads. */
export interface OperatorProfileFailure {
  readonly code: string;
  readonly status?: number;
  readonly reasonCode?: string;
  readonly detail?: string;
  readonly params?: Vars;
  /** D854: the server's `Retry-After` on a 429 (the api-client's `ApiError.retryAfterSeconds`). */
  readonly retryAfterSeconds?: number;
}

type Res<T> = Result<T, OperatorProfileFailure>;

/** The account as `GET /v1/me` answers it (the fields the page reads). */
export interface OperatorProfileMe {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly locale: string;
  readonly timezone?: string;
  readonly photoUrl?: string;
}

/** `runtime.auth` (api-client `AuthApi`): the account read and the own-fields write. */
export interface OperatorProfileAccountApi<M extends OperatorProfileMe, L extends string> {
  me(): Promise<Res<M>>;
  updateMe(patch: { readonly firstName?: string; readonly lastName?: string; readonly locale?: L }): Promise<Res<M>>;
}

export interface OperatorProfileSessionView {
  readonly id: string;
  readonly current: boolean;
  readonly device: { readonly platform: string; readonly model: string | null; readonly osName: string | null; readonly osVersion: string | null };
  readonly signedInAt: string;
  readonly lastActiveAt: string;
}

/** `runtime.operatorAuth` (api-client `OperatorAuthApi`): the operator's own photo, password, second factor and sessions. */
export interface OperatorProfileSelfApi {
  photo(): Promise<Res<{ readonly photo: { readonly url: string } | null }>>;
  setPhoto(bytesBase64: string): Promise<Res<{ readonly reasonCode: string }>>;
  removePhoto(): Promise<Res<{ readonly reasonCode: string }>>;
  changePassword(input: { readonly currentPassword: string; readonly newPassword: string; readonly code: string }): Promise<Res<{ readonly reasonCode: string }>>;
  mfaStatus(): Promise<Res<{ readonly mfa: string; readonly recoveryCodesRemaining: number }>>;
  regenerateRecoveryCodes(mfaCode: string): Promise<Res<{ readonly recoveryCodes: readonly string[] }>>;
  sessions(): Promise<Res<{ readonly sessions: readonly OperatorProfileSessionView[] }>>;
  revokeOtherSessions(): Promise<Res<{ readonly reasonCode: string }>>;
  revokeSession(sessionId: string): Promise<Res<{ readonly reasonCode: string }>>;
}

/** The slice of a console's `useVocab()` the page reads (both the Admin's hook and `createUseVocab` provide it). */
export interface OperatorProfileVocab {
  readonly v: (id: string, code: string | null | undefined, vars?: Vars) => string;
  readonly refusal: (surface: string, error: OperatorProfileFailure, vars?: Vars) => string | undefined;
}

export interface OperatorProfilePageProps<M extends OperatorProfileMe, L extends string> {
  readonly t: (id: OperatorProfileMessageId, vars?: Vars) => string;
  readonly vocab: OperatorProfileVocab;
  readonly account: OperatorProfileAccountApi<M, L>;
  readonly self: OperatorProfileSelfApi;
  /** The console's language now, the languages it ships and its switch (the i18n kit's `setLang`). */
  readonly lang: L;
  readonly langs: readonly L[];
  readonly setLang: (lang: L) => void;
  /** The account after a change (name, language, photo): the console updates its sidebar identity. */
  readonly onAccountChanged?: (me: M) => void;
  readonly initialTab?: 'profile' | 'preferences' | 'security' | 'sessions';
  /**
   * D854 (L7b-008): the account the console already read (`GET /v1/me` at sign-in, for its sidebar). Given, the page opens on it and does not
   * read `/v1/me` again - before D854 "Meu perfil" cost two reads per open. Absent (or still loading), the page reads it once itself.
   */
  readonly initialMe?: M;
}

type TabId = NonNullable<OperatorProfilePageProps<OperatorProfileMe, string>['initialTab']>;
type Msg = { readonly tone: 'success' | 'danger'; readonly text: string } | undefined;

interface Ctx {
  readonly t: (id: OperatorProfileMessageId, vars?: Vars) => string;
  readonly vocab: OperatorProfileVocab;
  readonly fail: (failure: OperatorProfileFailure) => string;
}

/**
 * D806 (doc 35 §4 "the account wins after sign-in"): once the account's saved language is known, the console follows it - only when it is
 * exactly one of the console's languages (the value `PATCH /v1/me` stores), and once per value, so a sign-up locale the operator never
 * chose does not override the browser and nothing fights a later choice.
 */
export function useAccountLanguage<L extends string>(locale: string | undefined, langs: readonly L[], lang: L, setLang: (lang: L) => void): void {
  const applied = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (locale === undefined || applied.current === locale) return;
    applied.current = locale;
    if ((langs as readonly string[]).includes(locale) && locale !== lang) setLang(locale as L);
  }, [locale, langs, lang, setLang]);
}

/** The full name of an account, or '' (the console's own fallback then applies). */
export function accountDisplayName(me: { readonly firstName?: string; readonly lastName?: string } | undefined): string {
  return [me?.firstName, me?.lastName].filter((p) => p !== undefined && p !== '').join(' ');
}

async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

function useObjectUrl(file: File | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    const create = typeof URL !== 'undefined' ? (URL as { createObjectURL?: (b: Blob) => string }).createObjectURL : undefined;
    if (file === undefined || create === undefined) {
      setUrl(undefined);
      return undefined;
    }
    const made = create.call(URL, file);
    setUrl(made);
    return () => URL.revokeObjectURL?.(made);
  }, [file]);
  return url;
}

function Status({ msg }: { readonly msg: Msg }): ReactElement | null {
  return msg === undefined ? null : <Notice tone={msg.tone}>{msg.text}</Notice>;
}

const SIX_DIGITS = /^\d{6}$/;

export function OperatorProfilePage<M extends OperatorProfileMe, L extends string>(props: OperatorProfilePageProps<M, L>): ReactElement {
  const { t, vocab, account, self, onAccountChanged } = props;
  const id = useId();
  const [tab, setTab] = useState<TabId>(props.initialTab ?? 'profile');
  const [me, setMe] = useState<M | undefined>(props.initialMe);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  // D854: the console's own read stands in for the page's first one (read once at mount: a later change comes back through `changed`).
  const hadInitial = useRef(props.initialMe !== undefined);

  const fail = useCallback((failure: OperatorProfileFailure): string => {
    // D854 (L7b-008, L4-024): a 429 that says when to try again is worded with that wait ("tente de novo em N s").
    if (failure.code === 'RATE_LIMITED' && failure.retryAfterSeconds !== undefined && Number.isFinite(failure.retryAfterSeconds)) {
      const seconds = Math.max(1, Math.ceil(failure.retryAfterSeconds));
      const timed = vocab.refusal(OPERATOR_PROFILE_REFUSAL_SURFACE, { code: 'RATE_LIMITED', reasonCode: OPERATOR_PROFILE_RETRY_IN }, { seconds });
      if (timed !== undefined) return timed;
    }
    return vocab.refusal(OPERATOR_PROFILE_REFUSAL_SURFACE, failure) ?? t('profile.error.generic');
  }, [vocab, t]);
  const ctx: Ctx = { t, vocab, fail };

  const load = useCallback(async (): Promise<void> => {
    setLoadError(undefined);
    const res = await account.me();
    if (res.ok) setMe(res.value);
    else setLoadError(fail(res.error));
  }, [account, fail]);

  useEffect(() => {
    if (hadInitial.current) return;
    void load();
  }, [load]);

  const changed = (next: M): void => {
    setMe(next);
    onAccountChanged?.(next);
  };

  const tabs = [
    { id: 'profile' as const, label: t('profile.tab.profile') },
    { id: 'preferences' as const, label: t('profile.tab.preferences') },
    { id: 'security' as const, label: t('profile.tab.security') },
    { id: 'sessions' as const, label: t('profile.tab.sessions') },
  ];

  let body: ReactNode;
  if (me === undefined && loadError !== undefined) {
    body = (
      <Stack gap={3} align="start">
        <Notice tone="danger">{`${t('profile.loadFailed')} ${loadError}`}</Notice>
        <Button onClick={() => void load()}>{t('profile.retry')}</Button>
      </Stack>
    );
  } else if (me === undefined) {
    body = <LoadingPanel label={t('profile.loading')} rows={4} />;
  } else {
    body = (
      <>
        <Tabs id={id} tabs={tabs} value={tab} onChange={setTab} ariaLabel={t('profile.tabs.aria')} />
        <TabPanel id={id} tab="profile" value={tab}>
          <ProfilePanel ctx={ctx} me={me} account={account} self={self} onChanged={changed} />
        </TabPanel>
        <TabPanel id={id} tab="preferences" value={tab}>
          <PreferencesPanel ctx={ctx} account={account} lang={props.lang} langs={props.langs} setLang={props.setLang} onChanged={changed} />
        </TabPanel>
        <TabPanel id={id} tab="security" value={tab}>
          <SecurityPanel ctx={ctx} self={self} />
        </TabPanel>
        <TabPanel id={id} tab="sessions" value={tab}>
          <SessionsPanel ctx={ctx} self={self} lang={props.lang} timeZone={me.timezone} />
        </TabPanel>
      </>
    );
  }

  return (
    <Page>
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />
      {body}
    </Page>
  );
}

// ---- Perfil -------------------------------------------------------------------------------------------------------------

function ProfilePanel<M extends OperatorProfileMe, L extends string>({ ctx, me, account, self, onChanged }: { readonly ctx: Ctx; readonly me: M; readonly account: OperatorProfileAccountApi<M, L>; readonly self: OperatorProfileSelfApi; readonly onChanged: (me: M) => void }): ReactElement {
  const { t, vocab, fail } = ctx;
  const id = useId();
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(me.photoUrl);
  const [file, setFile] = useState<File | undefined>(undefined);
  const preview = useObjectUrl(file);
  const [photoMsg, setPhotoMsg] = useState<Msg>(undefined);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [first, setFirst] = useState(me.firstName ?? '');
  const [last, setLast] = useState(me.lastName ?? '');
  const [nameMsg, setNameMsg] = useState<Msg>(undefined);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [nameBusy, setNameBusy] = useState(false);

  // A fresh signed link for the current photo (the one in `GET /v1/me` may be close to expiring).
  useEffect(() => {
    let live = true;
    void self.photo().then((res) => {
      if (live && res.ok) setPhotoUrl(res.value.photo?.url);
    });
    return () => {
      live = false;
    };
  }, [self]);

  const refreshAccount = async (): Promise<void> => {
    const res = await account.me();
    if (res.ok) onChanged(res.value);
  };

  const pick = (files: readonly File[]): void => {
    setPhotoMsg(undefined);
    const chosen = files[0];
    if (chosen !== undefined && chosen.type !== 'image/jpeg') {
      setFile(undefined);
      setPhotoMsg({ tone: 'danger', text: fail({ code: 'BAD_REQUEST', status: 400, reasonCode: 'OPERATOR_PHOTO_TYPE_INVALID' }) });
      return;
    }
    setFile(chosen);
  };

  const send = async (): Promise<void> => {
    if (file === undefined) return;
    setPhotoBusy(true);
    setPhotoMsg(undefined);
    const res = await self.setPhoto(await fileToBase64(file));
    if (!res.ok) {
      setPhotoBusy(false);
      setPhotoMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    const fresh = await self.photo();
    if (fresh.ok) setPhotoUrl(fresh.value.photo?.url);
    setFile(undefined);
    setPhotoBusy(false);
    setPhotoMsg({ tone: 'success', text: vocab.v('identity.operator_photo_decision', res.value.reasonCode) });
    await refreshAccount();
  };

  const remove = async (): Promise<void> => {
    setPhotoBusy(true);
    setPhotoMsg(undefined);
    const res = await self.removePhoto();
    setPhotoBusy(false);
    if (!res.ok) {
      setPhotoMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setPhotoUrl(undefined);
    setPhotoMsg({ tone: 'success', text: vocab.v('identity.operator_photo_decision', res.value.reasonCode) });
    await refreshAccount();
  };

  const saveName = async (): Promise<void> => {
    setNameMsg(undefined);
    const f = first.trim();
    const l = last.trim();
    if (f === '' || l === '') {
      setNameError(t('profile.name.required'));
      return;
    }
    setNameError(undefined);
    const patch = { ...(f !== (me.firstName ?? '') ? { firstName: f } : {}), ...(l !== (me.lastName ?? '') ? { lastName: l } : {}) };
    if (Object.keys(patch).length === 0) {
      setNameMsg({ tone: 'success', text: vocab.v('identity.operator_self_decision', 'PROFILE_SELF_UNCHANGED') });
      return;
    }
    setNameBusy(true);
    const res = await account.updateMe(patch);
    setNameBusy(false);
    if (!res.ok) {
      setNameMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setFirst(res.value.firstName ?? f);
    setLast(res.value.lastName ?? l);
    setNameMsg({ tone: 'success', text: vocab.v('identity.operator_self_decision', 'PROFILE_SELF_EDITED') });
    onChanged(res.value);
  };

  const removeSpec: ConfirmSpec = {
    title: t('profile.photo.removeConfirm.title'),
    body: t('profile.photo.removeConfirm.body'),
    confirmLabel: t('profile.photo.removeConfirm.confirm'),
    cancelLabel: t('profile.cancel'),
    tone: 'danger',
    icon: 'x',
  };

  const initials = initialsOf(accountDisplayName(me) || (me.email ?? ''));

  return (
    <Stack gap={4}>
      <Card as="section" aria-labelledby={`${id}-photo`}>
        <Stack gap={3}>
          <PanelHead title={t('profile.photo.title')} titleId={`${id}-photo`} dense />
          <Row gap={4} wrap>
            <Avatar initials={initials} size={96} photoUrl={preview ?? photoUrl} />
            <Stack gap={2}>
              {preview !== undefined && <Text role="caption" tone="secondary">{t('profile.photo.preview')}</Text>}
              <Field label={t('profile.photo.field')} htmlFor={`${id}-file`} hint={t('profile.photo.hint')}>
                <FileInput id={`${id}-file`} accept="image/jpeg" value={file !== undefined ? [file] : []} onChange={pick} chooseLabel={t('profile.photo.choose')} replaceLabel={t('profile.photo.replace')} emptyLabel={t('profile.photo.empty')} clearLabel={t('profile.photo.clear')} disabled={photoBusy} />
              </Field>
              <Row gap={2} wrap>
                <Button onClick={() => void send()} disabled={file === undefined} loading={photoBusy}>{t('profile.photo.send')}</Button>
                {photoUrl !== undefined && file === undefined && (
                  <Button variant="ghost" icon="x" onClick={() => setConfirmOpen(true)} disabled={photoBusy}>{t('profile.photo.remove')}</Button>
                )}
              </Row>
            </Stack>
          </Row>
          <Status msg={photoMsg} />
        </Stack>
      </Card>
      <Card as="section" aria-labelledby={`${id}-name`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void saveName();
          }}
        >
          <Stack gap={3}>
            <PanelHead title={t('profile.name.title')} titleId={`${id}-name`} dense />
            <Row gap={3} wrap align="start">
              <Field label={t('profile.name.first')} htmlFor={`${id}-first`} required error={nameError !== undefined && first.trim() === '' ? nameError : undefined}>
                <TextInput id={`${id}-first`} autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} />
              </Field>
              <Field label={t('profile.name.last')} htmlFor={`${id}-last`} required error={nameError !== undefined && last.trim() === '' ? nameError : undefined}>
                <TextInput id={`${id}-last`} autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} />
              </Field>
            </Row>
            <Field label={t('profile.email.label')} htmlFor={`${id}-email`} hint={t('profile.email.hint')}>
              <TextInput id={`${id}-email`} type="email" value={me.email ?? ''} readOnly />
            </Field>
            <Status msg={nameMsg} />
            <Row>
              <Button type="submit" variant="primary" loading={nameBusy}>{t('profile.name.save')}</Button>
            </Row>
          </Stack>
        </form>
      </Card>
      <ConfirmDialog
        spec={removeSpec}
        open={confirmOpen}
        onResolve={(r) => {
          setConfirmOpen(false);
          if (r.confirmed) void remove();
        }}
      />
    </Stack>
  );
}

// ---- Preferências -------------------------------------------------------------------------------------------------------

function PreferencesPanel<M extends OperatorProfileMe, L extends string>({ ctx, account, lang, langs, setLang, onChanged }: { readonly ctx: Ctx; readonly account: OperatorProfileAccountApi<M, L>; readonly lang: L; readonly langs: readonly L[]; readonly setLang: (lang: L) => void; readonly onChanged: (me: M) => void }): ReactElement {
  const { t, vocab, fail } = ctx;
  const id = useId();
  const [pref, setPref] = useThemePreference();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(undefined);

  const chooseLanguage = async (next: L): Promise<void> => {
    if (next === lang) return;
    setBusy(true);
    setMsg(undefined);
    const res = await account.updateMe({ locale: next });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setLang(next);
    onChanged(res.value);
    setMsg({ tone: 'success', text: t('profile.prefs.language.saved') });
  };

  return (
    <Stack gap={4}>
      <Card as="section" aria-labelledby={`${id}-theme`}>
        <Stack gap={3}>
          <PanelHead title={t('profile.prefs.theme')} titleId={`${id}-theme`} dense />
          <RadioGroup
            name={`${id}-theme-choice`}
            labelledBy={`${id}-theme`}
            options={THEME_PREFERENCES.map((p) => ({ value: p, label: t(`profile.prefs.theme.${p}`) }))}
            value={pref}
            onChange={(v) => setPref(v as ThemePreference)}
          />
        </Stack>
      </Card>
      <Card as="section" aria-labelledby={`${id}-lang`}>
        <Stack gap={3}>
          <PanelHead title={t('profile.prefs.language')} titleId={`${id}-lang`} dense />
          <Text role="caption" tone="secondary" block>{t('profile.prefs.language.hint')}</Text>
          <RadioGroup name={`${id}-lang-choice`} labelledBy={`${id}-lang`} options={langs.map((l) => ({ value: l, label: vocab.v('identity.operator_console_language', l) }))} value={lang} disabled={busy} onChange={(v) => void chooseLanguage(v as L)} />
          <Status msg={msg} />
        </Stack>
      </Card>
    </Stack>
  );
}

// ---- Segurança ----------------------------------------------------------------------------------------------------------

function SecurityPanel({ ctx, self }: { readonly ctx: Ctx; readonly self: OperatorProfileSelfApi }): ReactElement {
  const { t, vocab, fail } = ctx;
  const id = useId();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<{ readonly confirm?: string; readonly code?: string }>({});
  const [pwMsg, setPwMsg] = useState<Msg>(undefined);
  const [pwBusy, setPwBusy] = useState(false);

  const [mfa, setMfa] = useState<{ readonly mfa: string; readonly recoveryCodesRemaining: number } | undefined>(undefined);
  const [mfaMsg, setMfaMsg] = useState<Msg>(undefined);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaCodeError, setMfaCodeError] = useState<string | undefined>(undefined);
  const [codes, setCodes] = useState<readonly string[] | undefined>(undefined);
  const [codesBusy, setCodesBusy] = useState(false);

  const loadMfa = useCallback(async (): Promise<void> => {
    const res = await self.mfaStatus();
    if (res.ok) setMfa(res.value);
    else setMfaMsg({ tone: 'danger', text: fail(res.error) });
  }, [self, fail]);

  useEffect(() => {
    void loadMfa();
  }, [loadMfa]);

  const changePassword = async (): Promise<void> => {
    setPwMsg(undefined);
    if (current === '' || next === '') {
      setErrors({});
      setPwMsg({ tone: 'danger', text: fail({ code: 'BAD_REQUEST', status: 400, reasonCode: 'OPERATOR_PASSWORD_FIELDS_REQUIRED' }) });
      return;
    }
    const found = { ...(next !== confirm ? { confirm: t('profile.password.mismatch') } : {}), ...(!SIX_DIGITS.test(code.trim()) ? { code: t('profile.code.invalid') } : {}) };
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setPwBusy(true);
    const res = await self.changePassword({ currentPassword: current, newPassword: next, code: code.trim() });
    setPwBusy(false);
    if (!res.ok) {
      setPwMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setCurrent('');
    setNext('');
    setConfirm('');
    setCode('');
    setPwMsg({ tone: 'success', text: vocab.v('identity.operator_self_decision', res.value.reasonCode) });
  };

  const regenerate = async (): Promise<void> => {
    setMfaMsg(undefined);
    if (!SIX_DIGITS.test(mfaCode.trim())) {
      setMfaCodeError(t('profile.code.invalid'));
      return;
    }
    setMfaCodeError(undefined);
    setCodesBusy(true);
    const res = await self.regenerateRecoveryCodes(mfaCode.trim());
    setCodesBusy(false);
    if (!res.ok) {
      setMfaMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setMfaCode('');
    setCodes(res.value.recoveryCodes);
    await loadMfa();
  };

  const copy = async (): Promise<void> => {
    if (codes === undefined) return;
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setMfaMsg({ tone: 'success', text: t('profile.codes.copied') });
    } catch {
      setMfaMsg({ tone: 'danger', text: t('profile.codes.copyFailed') });
    }
  };

  const download = (): void => {
    if (codes === undefined) return;
    const create = typeof URL !== 'undefined' ? (URL as { createObjectURL?: (b: Blob) => string }).createObjectURL : undefined;
    if (create === undefined) return;
    const url = create.call(URL, new Blob([`${codes.join('\n')}\n`], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('profile.codes.fileName')}.txt`;
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL?.(url);
  };

  return (
    <Stack gap={4}>
      <Card as="section" aria-labelledby={`${id}-pw`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void changePassword();
          }}
        >
          <Stack gap={3}>
            <PanelHead title={t('profile.password.title')} titleId={`${id}-pw`} dense />
            <Field label={t('profile.password.current')} htmlFor={`${id}-current`} required>
              <TextInput id={`${id}-current`} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </Field>
            <Field label={t('profile.password.new')} htmlFor={`${id}-new`} hint={t('profile.password.newHint')} required>
              <TextInput id={`${id}-new`} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
            </Field>
            <Field label={t('profile.password.confirm')} htmlFor={`${id}-confirm`} error={errors.confirm} required>
              <TextInput id={`${id}-confirm`} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </Field>
            <Field label={t('profile.code.label')} htmlFor={`${id}-code`} hint={t('profile.code.hint')} error={errors.code} required>
              <TextInput id={`${id}-code`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="tnum" value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Status msg={pwMsg} />
            <Row>
              <Button type="submit" variant="primary" loading={pwBusy}>{t('profile.password.submit')}</Button>
            </Row>
          </Stack>
        </form>
      </Card>
      <Card as="section" aria-labelledby={`${id}-mfa`}>
        <Stack gap={3}>
          <PanelHead title={t('profile.mfa.title')} titleId={`${id}-mfa`} dense />
          {mfa !== undefined && (
            <>
              <KeyValue label={t('profile.mfa.state')}>
                <Badge tone={mfa.mfa === 'SEALED' ? 'success' : 'warning'}>{vocab.v('operator.mfa', mfa.mfa)}</Badge>
              </KeyValue>
              <Text role="body" tone="secondary" className="tnum">{t('profile.mfa.remaining', { n: mfa.recoveryCodesRemaining })}</Text>
              {mfa.mfa === 'SEALED' ? (
                codes === undefined && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void regenerate();
                    }}
                  >
                    <Stack gap={3}>
                      <Notice tone="warning">{t('profile.codes.warning')}</Notice>
                      <Field label={t('profile.code.label')} htmlFor={`${id}-mfa-code`} hint={t('profile.code.hint')} error={mfaCodeError} required>
                        <TextInput id={`${id}-mfa-code`} inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="tnum" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
                      </Field>
                      <Row>
                        <Button type="submit" loading={codesBusy}>{t('profile.codes.generate')}</Button>
                      </Row>
                    </Stack>
                  </form>
                )
              ) : (
                <Notice tone="neutral">{t('profile.mfa.notSealed')}</Notice>
              )}
            </>
          )}
          {mfa === undefined && mfaMsg === undefined && <LoadingPanel label={t('profile.loading')} rows={2} framed={false} />}
          {codes !== undefined && (
            <Stack gap={3} data-testid="profile-recovery-codes">
              <Text role="label" as="h3">{t('profile.codes.title')}</Text>
              <Notice tone="warning">{t('profile.codes.once')}</Notice>
              <CodeColumns>
                {codes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </CodeColumns>
              <Row gap={2} wrap>
                <Button icon="copy" onClick={() => void copy()}>{t('profile.codes.copy')}</Button>
                <Button icon="download" onClick={download}>{t('profile.codes.download')}</Button>
                <Button variant="ghost" onClick={() => setCodes(undefined)}>{t('profile.codes.done')}</Button>
              </Row>
            </Stack>
          )}
          <Status msg={mfaMsg} />
        </Stack>
      </Card>
    </Stack>
  );
}

// ---- Sessões ativas -----------------------------------------------------------------------------------------------------

function SessionsPanel({ ctx, self, lang, timeZone }: { readonly ctx: Ctx; readonly self: OperatorProfileSelfApi; readonly lang: string; readonly timeZone: string | undefined }): ReactElement {
  const { t, vocab, fail } = ctx;
  const id = useId();
  const [sessions, setSessions] = useState<readonly OperatorProfileSessionView[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoadError(undefined);
    const res = await self.sessions();
    if (res.ok) setSessions(res.value.sessions);
    else setLoadError(fail(res.error));
  }, [self, fail]);

  useEffect(() => {
    void load();
  }, [load]);

  const when = (iso: string): string => {
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) return '';
    try {
      return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', ...(timeZone !== undefined && timeZone !== '' ? { timeZone } : {}) }).format(new Date(ms));
    } catch {
      return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ms));
    }
  };

  const deviceText = (s: OperatorProfileSessionView): string => {
    const os = [s.device.osName, s.device.osVersion].filter((p): p is string => p !== null && p !== '').join(' ');
    return [vocab.v('common.client_platform', s.device.platform), s.device.model, os].filter((p): p is string => p !== null && p !== '').join(' · ');
  };

  const end = async (sessionId: string): Promise<void> => {
    setBusy(sessionId);
    setMsg(undefined);
    const res = await self.revokeSession(sessionId);
    setBusy(undefined);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setMsg({ tone: 'success', text: vocab.v('identity.operator_self_decision', res.value.reasonCode) });
    await load();
  };

  const endOthers = async (): Promise<void> => {
    setBusy('*');
    setMsg(undefined);
    const res = await self.revokeOtherSessions();
    setBusy(undefined);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: fail(res.error) });
      return;
    }
    setMsg({ tone: 'success', text: vocab.v('identity.operator_self_decision', res.value.reasonCode) });
    await load();
  };

  const others = (sessions ?? []).filter((s) => !s.current);
  const spec: ConfirmSpec = {
    title: t('profile.sessions.endAllConfirm.title'),
    body: t('profile.sessions.endAllConfirm.body'),
    confirmLabel: t('profile.sessions.endAllConfirm.confirm'),
    cancelLabel: t('profile.cancel'),
    tone: 'danger',
    icon: 'logOut',
  };

  return (
    <Card as="section" aria-labelledby={`${id}-sessions`}>
      <Stack gap={3}>
        <PanelHead
          title={t('profile.sessions.title')}
          titleId={`${id}-sessions`}
          dense
          actions={others.length > 0 ? <Button variant="danger" onClick={() => setConfirmOpen(true)} disabled={busy !== undefined}>{t('profile.sessions.endAll')}</Button> : undefined}
        />
        <Text role="caption" tone="secondary" block>{t('profile.sessions.subtitle')}</Text>
        {sessions === undefined && loadError === undefined && <LoadingPanel label={t('profile.loading')} rows={3} framed={false} />}
        {loadError !== undefined && (
          <Stack gap={3} align="start">
            <Notice tone="danger">{loadError}</Notice>
            <Button onClick={() => void load()}>{t('profile.retry')}</Button>
          </Stack>
        )}
        {sessions !== undefined && (
          <Stack as="ul" gap={2} className="vello-profile__sessions">
            {sessions.map((s) => {
              const device = deviceText(s);
              return (
                <li key={s.id} className="vello-profile__session">
                  <Stack gap={1}>
                    <Row gap={2} wrap>
                      <Text role="label">{device}</Text>
                      {s.current && <Badge tone="success">{t('profile.sessions.current')}</Badge>}
                    </Row>
                    <Text role="caption" tone="secondary" className="tnum">{t('profile.sessions.signedIn', { when: when(s.signedInAt) })}</Text>
                    <Text role="caption" tone="secondary" className="tnum">{t('profile.sessions.lastActive', { when: when(s.lastActiveAt) })}</Text>
                  </Stack>
                  {!s.current && (
                    <Button size="sm" aria-label={t('profile.sessions.endAria', { device })} onClick={() => void end(s.id)} loading={busy === s.id} disabled={busy !== undefined && busy !== s.id}>
                      {t('profile.sessions.end')}
                    </Button>
                  )}
                </li>
              );
            })}
          </Stack>
        )}
        {sessions !== undefined && others.length === 0 && <EmptyState title={t('profile.sessions.none')} />}
        <Status msg={msg} />
      </Stack>
      <ConfirmDialog
        spec={spec}
        open={confirmOpen}
        onResolve={(r) => {
          setConfirmOpen(false);
          if (r.confirmed) void endOthers();
        }}
      />
    </Card>
  );
}
